import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowLeft, FaClock, FaFire, FaTimes, FaHeart, FaRegHeart, FaSyncAlt, FaStar, FaThumbsDown } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import SupplementsBlock from '@/components/SupplementsBlock';
import FitEvolutionDownloadButton from '@/components/FitEvolutionDownloadButton';
import RecipeLibraryModal from '@/components/RecipeLibraryModal';
import CloseMacroCardWarning from '@/components/CloseMacroCardWarning';
import PlanMacrosSummary from '@/components/PlanMacrosSummary';
import { normalizeIngredientName, formatAmount, shouldExcludeIngredient } from '@/utils/ingredientDisplay';

// 2026-02 · Clasificación de ingredientes principales. Filtra el array
// `ingredients` de la receta y devuelve una lista para mostrar debajo de los
// macros: sólo proteína, carbohidrato, grasa (siempre) y fruta / vegetales
// (marcados como opcionales). Salsas, aderezos y especias quedan afuera.
const _norm = (s) => (s || '').toString().toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const _KW = {
  protein: ['pollo','pechuga','carne','res','ternera','cerdo','pavo','pescado','atun','salmon','merluza','tilapia','trucha','sardina','bacalao','langostino','camaron','marisco','huevo','clara','tofu','tempeh','seitan','soya','suero','whey','proteina','caseina','queso cottage','queso','cuajada','requeson','yogur griego','yogurt griego'],
  carb: ['arroz','avena','quinoa','papa','patata','platano','banana','banano','yuca','batata','camote','boniato','pasta','fideo','macarron','tallarin','pan','bagel','baguette','croissant','wrap','pita','muffin','waffle','tortilla','arepa','maiz','harina','granola','cereal','frijol','lenteja','garbanzo','poroto','judia','alubia','habichuela','grano'],
  fat: ['aguacate','palta','aceite','oliva','mantequilla','mani','cacahuate','almendra','nuez','nueces','castana','pistacho','avellana','coco','semilla','chia','linaza','sesamo','ajonjoli','tahini','crema de mani','crema de cacahuate'],
  fruit: ['manzana','pera','fresa','frutilla','arandano','frambuesa','mora','uva','pina','ananas','mango','papaya','melon','sandia','durazno','melocoton','ciruela','naranja','mandarina','limon','lima','pomelo','kiwi','higo','datil','cereza','guayaba','maracuya','fruta'],
  veg: ['espinaca','lechuga','arugula','rucula','kale','acelga','brocoli','coliflor','zanahoria','tomate','pepino','pimiento','pimenton','morron','cebolla','ajo','apio','calabacin','calabaza','zapallo','berenjena','esparrago','col','repollo','remolacha','betarraga','rabano','champinon','hongo','vegetal','verdura','ensalada'],
  skip: ['salsa','aderezo','vinagreta','mayonesa','mostaza','ketchup','sal','pimienta','curcuma','pimenton dulce','oregano','albahaca','tomillo','romero','comino','canela','vainilla','edulcorante','stevia','endulzante','azucar','miel','agua','hielo','esencia','extracto','colorante','vinagre','soya salsa','soja salsa','sazonador','condimento','especia'],
};
function _classifyIngredient(name) {
  const n = _norm(name);
  if (!n) return null;
  // 2026-02 · Cualquier "jugo" o "zumo" se descarta (aderezo), incluso
  // variantes como "Limón (jugo)", "Limón o lima (jugo)", "Limón (ralladura
  // y jugo)", "Jugo de tomate", etc.
  if (/\bjugos?\b/.test(n) || /\bzumos?\b/.test(n)) return null;
  // Word-boundary match con plural opcional (`frijoles` matchea `frijol`,
  // `almendras` matchea `almendra`, soporta -s y -es).
  const hit = (kw) => new RegExp(`\\b${kw.replace(/[.*+?^${}()|[\\]\\\\]/g,'\\\\$&')}(?:es|s)?\\b`).test(n);
  // 2026-09 - un ingrediente principal puede contener tambien una palabra
  // 'skip' (ej. proteina en polvo sabor vainilla); la categoria real gana.
  for (const kw of _KW.protein) if (hit(kw)) return 'protein';
  for (const kw of _KW.carb) if (hit(kw)) return 'carb';
  for (const kw of _KW.fat) if (hit(kw)) return 'fat';
  for (const kw of _KW.fruit) if (hit(kw)) return 'fruit';
  for (const kw of _KW.veg) if (hit(kw)) return 'veg';
  for (const kw of _KW.skip) if (hit(kw)) return null;
  return null;
}
function MainIngredientsList({ ingredients, mainIngredients }) {
  // 2026-02 · Preferimos `main_ingredients` precomputado en backend cuando
  // existe (agrupa TODOS los ingredientes por categoría). Fallback al
  // clasificador local por si la receta viene de antes del backfill.
  let groups = Array.isArray(mainIngredients) && mainIngredients.length > 0
    ? mainIngredients
    : null;
  if (!groups) {
    if (!Array.isArray(ingredients) || ingredients.length === 0) return null;
    const buckets = { protein: [], carb: [], fat: [], fruit: [], veg: [] };
    for (const ing of ingredients) {
      const cat = _classifyIngredient(ing?.name);
      if (!cat) continue;
      buckets[cat].push({ name: ing.name, amount: ing.amount, hint: ing.amount_hint });
    }
    const LABELS = { protein: 'Proteína', carb: 'Carbohidrato', fat: 'Grasa', fruit: 'Fruta', veg: 'Vegetal' };
    const OPT = new Set(['fruit', 'veg']);
    groups = ['protein','carb','fat','fruit','veg']
      .filter(c => buckets[c].length > 0)
      .map(c => ({ category: c, category_label: LABELS[c], optional: OPT.has(c), items: buckets[c] }));
  }
  // 2026-02 · Aplica el filtro estricto en la tarjeta (mismas reglas que el PDF):
  // sólo alimentos principales, ocultando condimentos, salsas, aceites <10g,
  // cualquier ingrediente <10g, opcionales y vegetales <30g.
  const filteredGroups = (groups || [])
    .map(g => ({
      ...g,
      items: (g.items || []).filter(it => !shouldExcludeIngredient(it.name, it.amount)),
    }))
    .filter(g => g.items.length > 0);
  if (filteredGroups.length === 0) return null;
  return (
    <ul className="mb-3 space-y-1" data-testid="main-ingredients">
      {filteredGroups.map((g, gi) => (
        <li key={gi} className="text-[11px] leading-tight">
          <p className="text-white/85 font-bold uppercase tracking-wider text-[10px]">
            {g.category_label}{g.optional ? <span className="text-white/50 italic normal-case font-normal ml-1">(opcional)</span> : null}
          </p>
          <ul className="pl-0.5 space-y-0.5">
            {g.items.map((it, ii) => (
              <li key={ii} className="text-white/75 flex items-baseline gap-1.5">
                <span className="text-pink-400">•</span>
                <span className="capitalize">{normalizeIngredientName(it.name)}</span>
                {it.amount ? <strong className="text-white font-bold">({formatAmount(it.amount)})</strong> : null}
                {it.hint ? <span className="text-white/50">{it.hint}</span> : null}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}


// 2026-02 · Bloque de comunidad (WhatsApp/Telegram). Se muestra dentro de
// la sección de suplementos si el admin activó al menos un canal. Cada
// canal se controla independientemente desde el admin panel.
function CommunityBlock() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/personalized-plans/fit-evolution/community-config`);
        if (alive) setCfg(res.data || null);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);
  if (!cfg) return null;
  const waOn = !!cfg?.whatsapp?.enabled;
  const tgOn = !!cfg?.telegram?.enabled;
  if (!waOn && !tgOn) return null;
  return (
    <div
      className="rounded-2xl p-4 md:p-5"
      style={{
        background: 'linear-gradient(135deg, rgba(34,197,94,0.10), rgba(59,130,246,0.10))',
        border: '1px solid rgba(34,197,94,0.35)',
      }}
      data-testid="community-block"
    >
      <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: '#86efac' }}>
        Comunidad
      </p>
      <h4 className="text-base md:text-lg font-black text-white mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
        🤝 Únete a nuestra comunidad
      </h4>
      <p className="text-xs text-white/70 mb-3">
        Conecta con otras personas del programa y recibe motivación diaria.
      </p>
      <div className="flex flex-col gap-2">
        {waOn && (
          <a
            href={cfg.whatsapp.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white transition-all hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
            data-testid="community-whatsapp-btn"
          >
            💬 Unirme al WhatsApp
          </a>
        )}
        {tgOn && (
          <a
            href={cfg.telegram.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white transition-all hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #2AABEE, #229ED9)' }}
            data-testid="community-telegram-btn"
          >
            ✈️ Unirme al Telegram
          </a>
        )}
      </div>
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

const MEAL_TIMES_LEGACY = [
  { id: 'Desayuno', label: 'Desayuno', emoji: '🌅' },
  { id: 'Snack AM', label: 'Snack AM', emoji: '🥤' },
  { id: 'Almuerzo', label: 'Almuerzo', emoji: '🍽️' },
  { id: 'Snack PM', label: 'Snack PM', emoji: '🍎' },
  { id: 'Cena', label: 'Cena', emoji: '🌙' },
  { id: 'Post-Entreno', label: 'Post-Entreno', emoji: '💪' },
];

// Alias para batches con nombres legacy — SIEMPRE resuelven a los 6 momentos canónicos.
const MEAL_ALIASES = {
  'Post-entreno': 'Post-Entreno',
  'Post-entrenamiento': 'Post-Entreno',
  'Pre-entreno': 'Snack AM',
  'Pre-entrenamiento': 'Snack AM',
  'Merienda': 'Snack AM',
  'Merienda AM': 'Snack AM',
  'Merienda PM': 'Snack PM',
  'Snack': 'Snack AM',
};

// Emoji por posición para el estilo nuevo "Comida N"
const COMIDA_EMOJI = ['🌅', '🥤', '🍽️', '🥗', '⚡', '💪', '🌙'];

export default function FitEvolutionRecipes({ user }) {
  const navigate = useNavigate();
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [allBatchesByMeal, setAllBatchesByMeal] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // 'session' | 'network' | null
  const [detail, setDetail] = useState(null);
  const [images, setImages] = useState({});
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [favoritesList, setFavoritesList] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [regenerating, setRegenerating] = useState({}); // { recipe_id: true }
  const [canRegenerate, setCanRegenerate] = useState(false);
  // 2026-02 · Biblioteca de recetas (misma UX que MyDiet.jsx). Permite al
  // usuario Fit Evolution buscar por ingrediente ("papa", "crema de arroz"…)
  // y agregar una receta como opción extra para esa comida.
  const [recipeLibrary, setRecipeLibrary] = useState(null); // { mealIndex, mealTime } | null

  useEffect(() => {
    axios.get(`${API}/api/fit-evolution/user-features`)
      .then(r => setCanRegenerate(!!r.data?.allow_user_regenerate))
      .catch(() => setCanRegenerate(false));
  }, []);
  const pollRef = useRef(null);
  const inflightImgRef = useRef(new Set());

  const fetchAllBatches = useCallback(async () => {
    if (!user?.email) return;
    try {
      setLoadError(null);
      const res = await axios.get(`${API}/api/personalized-plans/fit-evolution/recipes/${encodeURIComponent(user.email)}`);
      const batches = res.data?.batches || [];
      const byMeal = {};
      for (const b of batches) {
        // Nuevos batches vienen con meal_time = "Comida N"; legacy con "Desayuno"/"Post-entreno"…
        const rawId = b.meal_time;
        const normalizedId = MEAL_ALIASES[rawId] || rawId;
        if (!byMeal[normalizedId]) byMeal[normalizedId] = { ...b, meal_time: normalizedId };
      }
      setAllBatchesByMeal(byMeal);
    } catch (e) {
      console.error(e);
      // 401/403 o fallo de red NO es "plan en preparación": se muestra el error real.
      setLoadError(e?.response?.status === 401 ? 'session' : 'network');
    }
    finally { setLoading(false); }
  }, [user]);

  const fetchFavorites = useCallback(async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(`${API}/api/personalized-plans/fit-evolution/favorite-recipes/${encodeURIComponent(user.email)}`);
      setFavoriteIds(res.data?.recipe_ids || []);
      setFavoritesList(res.data?.favorites || []);
    } catch (e) { console.error(e); }
  }, [user]);

  useEffect(() => { fetchAllBatches(); fetchFavorites(); }, [fetchAllBatches, fetchFavorites]);

  // Suplementación: un único fetch para los 4 momentos; se pasa a cada SupplementsBlock.
  const [supplements, setSupplements] = useState(null);
  useEffect(() => {
    if (!user?.email) return;
    let alive = true;
    axios.get(`${API}/api/personalized-plans/fit-evolution/supplements/${encodeURIComponent(user.email)}`)
      .then(res => { if (alive) setSupplements(res.data || null); })
      .catch(() => { if (alive) setSupplements(null); });
    return () => { alive = false; };
  }, [user?.email]);
  const supplementsByMoment = supplements?.uses_supplements ? (supplements.by_moment || {}) : {};
  const hasSupplements = ['en_ayunas', 'pre_entreno', 'intra_entreno', 'antes_dormir', 'con_comida_1', 'con_comida_2', 'con_comida_3', 'con_comida_4', 'con_comida_5']
    .some(m => (supplementsByMoment[m] || []).length > 0);

  // Tabs disponibles = derivadas de los batches del usuario.
  // Soporta el nuevo formato "Comida 1..N" (ordenado por meal_position) y
  // el legacy fijo (Desayuno, Almuerzo, ...) por retro-compatibilidad.
  const availableMeals = (() => {
    const batches = Object.values(allBatchesByMeal);
    if (batches.length === 0) return [];
    const isNewFormat = batches.some(b => typeof b.meal_time === 'string' && /^comida\s+\d+/i.test(b.meal_time));
    if (isNewFormat) {
      return batches
        .slice()
        .sort((a, b) => (a.meal_position || 0) - (b.meal_position || 0))
        .map(b => {
          const pos = b.meal_position || parseInt((b.meal_time.match(/\d+/) || ['0'])[0], 10) || 1;
          const emoji = b.is_post_workout ? '💪' : (COMIDA_EMOJI[pos - 1] || '🍽️');
          const label = b.is_post_workout ? `${b.meal_time} · Post-Entreno` : b.meal_time;
          return { id: b.meal_time, label, emoji, is_post_workout: !!b.is_post_workout };
        });
    }
    // Legacy path: mapeo fijo
    return MEAL_TIMES_LEGACY.filter(m => allBatchesByMeal[m.id]);
  })();
  const currentIsPostWorkout = availableMeals.find(m => m.id === selectedMeal)?.is_post_workout
    || selectedMeal === 'Post-Entreno';
  // Al primer render (o si el meal seleccionado ya no existe), preseleccionamos el primero disponible
  useEffect(() => {
    if (loading) return;
    if (!selectedMeal || !allBatchesByMeal[selectedMeal]) {
      if (availableMeals.length > 0) setSelectedMeal(availableMeals[0].id);
    }
  }, [loading, selectedMeal, allBatchesByMeal, availableMeals]);

  const currentBatch = selectedMeal ? allBatchesByMeal[selectedMeal] : null;

  // Respaldo lazy: si una receta no tiene `image_url` pero sí `has_image`
  // (legacy con base64 en la BD), pedimos SOLO su foto al endpoint puntual
  // `/recipes/image/{batch}/{recipe}`. Así el listado sigue liviano y no hay
  // cuadros vacíos para las alumnas.
  useEffect(() => {
    if (!currentBatch) return;
    const batchId = currentBatch.id;
    (currentBatch.recipes || []).forEach(async (r) => {
      if (r.image_url) return;          // ya viene la URL en el batch
      if (!r.has_image) return;         // no hay foto guardada en BD
      if (images[r.id]) return;         // ya está en cache local
      // Ticket 280983: este efecto se re-ejecuta cada vez que llega UNA foto; sin esto
      // se volvia a pedir (varios MB en base64) cada foto pendiente, una y otra vez.
      if (inflightImgRef.current.has(r.id)) return;
      inflightImgRef.current.add(r.id);
      try {
        const res = await axios.get(`${API}/api/personalized-plans/fit-evolution/recipes/image/${batchId}/${r.id}`);
        const url = (res.data?.image_url || '').trim();
        const b64 = res.data?.image_base64;
        let src = null;
        if (url) {
          src = url.startsWith('http') ? url : (url.startsWith('//') ? `https:${url}` : `https://${url.replace(/^\/+/, '')}`);
        } else if (b64) {
          src = b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
        }
        if (src) setImages(prev => ({ ...prev, [r.id]: src }));
      } catch { inflightImgRef.current.delete(r.id); /* silencioso — mostrará el ícono estático */ }
    });
  }, [currentBatch, images]);

  // Polling DESACTIVADO por defecto: el plan es estático una vez generado.
  // Solo se refresca cuando la usuaria presione 'Regenerar' explícitamente.
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [currentBatch]);

  const toggleFavorite = async (recipe) => {
    if (!user?.email || !currentBatch) return;
    try {
      const res = await axios.post(`${API}/api/personalized-plans/fit-evolution/toggle-favorite-recipe`, {
        email: user.email, batch_id: currentBatch.id, recipe_id: recipe.id,
      });
      if (res.data?.is_favorite) {
        setFavoriteIds(prev => [...prev, recipe.id]);
        toast.success('Receta guardada en favoritas');
      } else {
        setFavoriteIds(prev => prev.filter(id => id !== recipe.id));
        toast('Quitada de favoritas');
      }
      fetchFavorites();
    } catch (e) {
      toast.error('No pudimos guardar el favorito');
    }
  };

  const regenerateOne = async (recipe, { permanent = false } = {}) => {
    if (!user?.email || !currentBatch) return;
    if (regenerating[recipe.id]) return;
    const confirmMsg = permanent
      ? `¿Marcar "${recipe.name}" como que NO te gusta? Nunca volverá a aparecer en tu plan.`
      : `¿Cambiar "${recipe.name}" por otra receta? La actual podrá volver a aparecer más adelante.`;
    if (!window.confirm(confirmMsg)) return;
    setRegenerating(prev => ({ ...prev, [recipe.id]: true }));
    try {
      const res = await axios.post(`${API}/api/personalized-plans/fit-evolution/regenerate-recipe`, {
        email: user.email,
        batch_id: currentBatch.id,
        recipe_id: recipe.id,
        permanent,
      });
      if (res.data?.success && res.data?.recipe) {
        toast.success(permanent ? '¡Receta bloqueada!' : '¡Receta reemplazada!');
        // Actualizar en local
        setAllBatchesByMeal(prev => {
          const updated = { ...prev };
          const batch = { ...updated[selectedMeal] };
          batch.recipes = (batch.recipes || []).map(r => r.id === recipe.id ? res.data.recipe : r);
          updated[selectedMeal] = batch;
          return updated;
        });
        // Limpia imagen antigua
        setImages(prev => { const c = { ...prev }; delete c[recipe.id]; return c; });
        // La imagen de la nueva se generará en background — poll refrescará
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (permanent ? 'No pudimos bloquear' : 'No pudimos rotar'));
    } finally {
      setRegenerating(prev => { const c = { ...prev }; delete c[recipe.id]; return c; });
    }
  };

  const [regeneratingBatch, setRegeneratingBatch] = useState(false);
  const regenerateBatch = async () => {
    if (!user?.email || !currentBatch) return;
    if (!window.confirm(`¿Reemplazar TODAS las 6 recetas de ${selectedMeal}? Se descartarán y generaremos otras 6 diferentes.`)) return;
    setRegeneratingBatch(true);
    try {
      const res = await axios.post(`${API}/api/personalized-plans/fit-evolution/regenerate-batch`, {
        email: user.email, batch_id: currentBatch.id,
      });
      if (res.data?.success && res.data?.recipes) {
        toast.success('¡6 recetas nuevas generadas!');
        setAllBatchesByMeal(prev => ({ ...prev, [selectedMeal]: { ...prev[selectedMeal], recipes: res.data.recipes } }));
        setImages({});
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No pudimos regenerar el bloque');
    } finally {
      setRegeneratingBatch(false);
    }
  };

  const openDetail = (r) => setDetail({ ...r, batchId: currentBatch?.id, image: r.image_url || images[r.id] });

  return (
    <div className="min-h-screen py-8 px-4 md:px-6" style={{ backgroundColor: '#0a0a0a' }} data-testid="fit-evo-recipes">
      <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 15% 85%, rgba(var(--brand-primary-glow),0.14), transparent 55%), radial-gradient(circle at 85% 15%, rgba(0,240,255,0.10), transparent 45%)' }} />
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="back-to-dashboard">
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          <button
            onClick={() => setShowFavorites(v => !v)}
            data-testid="toggle-favorites"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all"
            style={{ background: showFavorites ? 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' : 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white' }}
          >
            <FaStar /> Mis favoritas ({favoritesList.length})
          </button>
        </div>

        <div className="text-center mb-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: 'var(--brand-primary)' }}>Fit Evolution · Cocina Inteligente</p>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }} data-testid="page-title">Mi Plan de Alimentación</h1>
          <p className="text-gray-300 text-sm md:text-base">6 opciones por cada comida — todas con los mismos macros, cocinas y estilos.</p>
          {currentIsPostWorkout && !showFavorites && (
            <>
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold" style={{ background: 'rgba(var(--brand-primary-glow),0.15)', border: '1px solid rgba(var(--brand-primary-glow),0.4)', color: '#FFD1E0' }} data-testid="post-workout-badge">
                💪 Post-Entreno · 3 con proteína en polvo · 3 con alimentos enteros · Sin grasas añadidas
              </div>
              <div className="mt-3 mx-auto max-w-md rounded-xl px-4 py-3 text-xs" style={{ background: 'rgba(251, 191, 36, 0.12)', border: '1px solid rgba(251, 191, 36, 0.35)', color: '#FDE68A' }} data-testid="post-workout-rest-day-note">
                <span className="font-bold">⚠️ Nota importante:</span>{' '}
                En tu <span className="font-bold">día de descanso</span> no se consume esta comida post-entreno. Solo la incluyes los días que entrenas.
              </div>
            </>
          )}
        </div>

        {/* Resumen de macros totales del plan (arriba, antes de las comidas) */}
        {!showFavorites && <PlanMacrosSummary batches={Object.values(allBatchesByMeal || {})} />}

        {/* ═════ SUPLEMENTACIÓN — bloques por momento del día (colapsable, cerrado por defecto) ═════ */}
        {hasSupplements && (
          <details
            className="rounded-2xl overflow-hidden mb-4"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}
            data-testid="banner-suplementacion"
          >
            <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between text-white font-bold text-sm md:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <span>💊 SUPLEMENTACIÓN</span>
              <span className="text-pink-400 text-lg">▾</span>
            </summary>
            <div className="px-4 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="supplements-wrapper">
                <SupplementsBlock moment="en_ayunas" items={supplementsByMoment.en_ayunas} note={supplements?.note} />
                <SupplementsBlock moment="pre_entreno" items={supplementsByMoment.pre_entreno} note={supplements?.note} />
                <SupplementsBlock moment="intra_entreno" items={supplementsByMoment.intra_entreno} note={supplements?.note} />
                <SupplementsBlock moment="antes_dormir" items={supplementsByMoment.antes_dormir} note={supplements?.note} />
                <SupplementsBlock moment="con_comida_1" items={supplementsByMoment.con_comida_1} note={supplements?.note} />
                <SupplementsBlock moment="con_comida_2" items={supplementsByMoment.con_comida_2} note={supplements?.note} />
                <SupplementsBlock moment="con_comida_3" items={supplementsByMoment.con_comida_3} note={supplements?.note} />
                <SupplementsBlock moment="con_comida_4" items={supplementsByMoment.con_comida_4} note={supplements?.note} />
                <SupplementsBlock moment="con_comida_5" items={supplementsByMoment.con_comida_5} note={supplements?.note} />
              </div>
            </div>
          </details>
        )}
        {/* 2026-02: bloque de comunidad eliminado del Plan de Alimentación por decisión del admin.
            El banner de comunidad sigue visible en el dashboard de inicio de Fit Evolution. */}

        {/* Selector — solo tabs disponibles */}
        {!showFavorites && availableMeals.length > 0 && (
          <>
            {/* 2026-02 · Banners colapsables informativos sobre la alimentación */}
            <div className="mb-4 space-y-2">
              <details
                className="rounded-2xl overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}
                data-testid="banner-como-utilizar"
              >
                <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between text-white font-bold text-sm md:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <span>🍽️ ¿CÓMO UTILIZAR TU ALIMENTACIÓN?</span>
                  <span className="text-pink-400 text-lg">▾</span>
                </summary>
                <div className="px-4 pb-4 text-white/80 text-sm leading-relaxed space-y-2">
                  <p>Tu alimentación está organizada por recetas para hacerla más fácil, práctica y sencilla de mantener en el tiempo.</p>
                  <p><strong className="text-white">Elige 1 opción por comida</strong> — Tienes 3 opciones para cada comida. Elige la que más te guste o la que te resulte más fácil.</p>
                  <p><strong className="text-white">¿No te gusta alguna receta?</strong> — Puedes cambiarla fácilmente utilizando el botón <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px]" style={{ background: 'rgba(255,255,255,0.15)' }}>↻</span> que aparece junto a cada opción.</p>
                  <p><strong className="text-white">No tienes que preparar la receta exactamente igual</strong> — La receta es una idea de preparación. Puedes tomar los alimentos principales y prepararlos de la forma que prefieras.</p>
                  <p><strong className="text-white">Revisa tus macronutrientes</strong> — Debajo de cada opción encontrarás los gramos que debes comer de proteína, carbohidratos y grasas que corresponden a esa comida.</p>
                  <p className="text-pink-300 italic">💗 Las recetas están diseñadas para darte variedad y facilitarte el proceso, no para complicarte.</p>
                </div>
              </details>

              <details
                className="rounded-2xl overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}
                data-testid="banner-como-pesar"
              >
                <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between text-white font-bold text-sm md:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <span>📌 NOTA IMPORTANTE SOBRE COMO PESAR TUS ALIMENTOS</span>
                  <span className="text-pink-400 text-lg">▾</span>
                </summary>
                <div className="px-4 pb-4 text-white/80 text-sm leading-relaxed space-y-2">
                  <p>Para que tus cantidades sean correctas, ten en cuenta:</p>
                  <p><strong className="text-white">🥩 Proteínas:</strong> pollo, carne y pescado se pesan <strong>en crudo</strong>, antes de cocinar.</p>
                  <p><strong className="text-white">🥤 Proteína en polvo:</strong> se pesa tal cual viene <strong>en polvo</strong>, antes de mezclarla.</p>
                  <p><strong className="text-white">🍚 Carbohidratos:</strong> arroz, papa, plátano y similares se pesan <strong>cocidos</strong>.</p>
                  <p><strong className="text-white">🌾 Harinas y cereales:</strong> avena, crema de arroz, harinas y cereales se pesan <strong>en seco</strong>, antes de cocinar o preparar.</p>
                  <p><strong className="text-white">🫘 Granos y legumbres:</strong> fríjoles, lentejas, garbanzos y similares se pesan <strong>en crudo/seco</strong>, salvo que tu plan indique específicamente que la cantidad corresponde al alimento cocido.</p>
                </div>
              </details>
            </div>

            {/* 2026-02 · Botón Descargar mi plan (justo encima de la lista de comidas) */}
            {!showFavorites && availableMeals.length > 0 && (
              <div className="flex justify-center mb-4" data-testid="fit-evo-download-wrapper">
                <FitEvolutionDownloadButton
                  userEmail={user?.email}
                  userName={user?.name}
                  planNameFallback="Mi Plan Fit Evolution"
                  batches={Object.values(allBatchesByMeal || {})}
                />
              </div>
            )}
            <div className="flex flex-wrap gap-2 mb-6 justify-center" data-testid="meal-selector">
              {availableMeals.map(m => (
                <button key={m.id}
                  onClick={() => setSelectedMeal(m.id)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${selectedMeal === m.id ? 'text-white scale-105' : 'text-white/70 hover:text-white'}`}
                  style={{ background: selectedMeal === m.id ? 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' : 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                  data-testid={`meal-tab-${m.id}`}>
                  {m.emoji} {m.label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Loading inicial */}
        {loading && (
          <div className="text-center rounded-2xl p-10 mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="loading-plan">
            <div className="inline-block w-10 h-10 rounded-full animate-pulse mb-3" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }} />
            <p className="text-white/80">Cargando tu plan de alimentación...</p>
          </div>
        )}

        {/* Sin batches todavía */}
        {!loading && loadError && (
          <div className="text-center rounded-2xl p-10 mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,80,80,0.4)' }} data-testid="plan-load-error">
            <h3 className="text-white text-xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {loadError === 'session' ? 'Tu sesión expiró' : 'No pudimos cargar tu plan'}
            </h3>
            <p className="text-white/70 max-w-md mx-auto mb-6">
              {loadError === 'session' ? 'Inicia sesión de nuevo para ver tu plan.' : 'Revisa tu conexión e inténtalo otra vez.'}
            </p>
            <button
              onClick={() => {
                if (loadError === 'session') {
                  try { localStorage.removeItem('deisycfit_token'); } catch (e) { /* noop */ }
                  navigate('/login');
                } else { setLoading(true); fetchAllBatches(); }
              }}
              data-testid="plan-load-error-btn"
              className="px-5 py-2.5 rounded-full text-white text-sm font-bold hover:scale-105 transition-all"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
            >
              {loadError === 'session' ? 'Iniciar sesión' : 'Reintentar'}
            </button>
          </div>
        )}

        {!loading && !loadError && availableMeals.length === 0 && !showFavorites && (
          <div className="text-center rounded-2xl p-10 mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.15)' }} data-testid="preparing-recipes">
            <div className="inline-block w-14 h-14 rounded-full animate-pulse mb-4" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }} />
            <h3 className="text-white text-xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>Estamos preparando tu plan</h3>
            <p className="text-white/70 max-w-md mx-auto mb-6">Tu Cocina Inteligente estará lista en unos minutos. Puedes cerrar esta ventana y volver más tarde.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => navigate('/macro-calculator')}
                data-testid="go-fill-form-btn"
                className="px-5 py-2.5 rounded-full text-white text-sm font-bold hover:scale-105 transition-all"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
              >
                Llenar / actualizar mi formulario
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                data-testid="go-dashboard-btn"
                className="px-5 py-2.5 rounded-full text-white/90 text-sm font-semibold hover:bg-white/10 transition-all"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}
              >
                Volver al Dashboard
              </button>
            </div>
          </div>
        )}

        {/* FAVORITAS view */}
        <AnimatePresence>
          {showFavorites && !loading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {favoritesList.length === 0 ? (
                <div className="text-center rounded-2xl p-10" style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.15)' }} data-testid="no-favorites">
                  <FaHeart className="text-4xl text-pink-400/50 mx-auto mb-3" />
                  <h3 className="text-white text-lg font-bold mb-1">Aún no tienes recetas favoritas</h3>
                  <p className="text-white/60 text-sm">Toca el corazón de cualquier receta para guardarla aquí.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="favorites-grid">
                  {favoritesList.map((f, i) => (
                    <FavoriteCard key={f.recipe_id} f={f} idx={i} onOpen={(img) => setDetail({ ...(f.recipe), image: img || f.recipe?.image_url || null })} />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* GRID recetas del meal actual */}
        {!showFavorites && currentBatch && currentBatch.recipes?.length > 0 && (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentBatch.recipes.map((r, i) => {
              // Prioridad: image_url (Cloudinary) > image_base64 embebido (legacy) > cache local > sin foto.
              // Reparación defensiva: si viene una URL sin protocolo la prefijeamos con https:// para que <img src> no la ignore.
              let img = null;
              const rawUrl = (r.image_url || '').trim();
              if (rawUrl) {
                img = rawUrl.startsWith('http') ? rawUrl : (rawUrl.startsWith('//') ? `https:${rawUrl}` : `https://${rawUrl.replace(/^\/+/, '')}`);
              } else if (r.image_base64) {
                // Segunda opción para batches legacy con base64 embebido.
                img = r.image_base64.startsWith('data:') ? r.image_base64 : `data:image/jpeg;base64,${r.image_base64}`;
              } else if (images[r.id]) {
                img = images[r.id];
              }
              const isFav = favoriteIds.includes(r.id);
              return (
                <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="rounded-2xl overflow-hidden relative" style={{ background: 'rgba(26,26,26,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid={`recipe-card-${i}`}>
                  {/* Foto */}
                  <div className="h-48 relative bg-black/40 flex items-center justify-center overflow-hidden"
                       style={{ background: img ? `url(${img}) center/cover` : 'linear-gradient(135deg, rgba(var(--brand-primary-glow),0.15), rgba(0,240,255,0.06))' }}>
                    {!img && (
                      // Sin URL guardada: mostramos ícono estático. NUNCA un loader —
                      // el plan es estático y las fotos vienen embebidas con image_url.
                      <div className="flex flex-col items-center gap-2 text-white/40 text-3xl">
                        🍽️
                      </div>
                    )}
                    {/* Corazón favorito */}
                    <button onClick={(e) => { e.stopPropagation(); toggleFavorite(r); }}
                      className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
                      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}
                      data-testid={`fav-btn-${i}`}>
                      {isFav ? <FaHeart className="text-pink-400" /> : <FaRegHeart className="text-white/80" />}
                    </button>
                    {r.uses_protein_powder && (
                      <span className="absolute bottom-2 left-2 text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,240,255,0.85)', color: '#001217' }}>
                        Proteína en polvo
                      </span>
                    )}
                  </div>
                  {/* Info */}
                  <div className="p-4">
                    <h3 className="text-white font-black text-base mb-2 line-clamp-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{r.name}</h3>
                    <div className="flex items-center gap-3 text-[11px] text-white/70 mb-3">
                      <span className="flex items-center gap-1"><FaClock /> {r.prep_minutes} min</span>
                      <span className="flex items-center gap-1"><FaFire /> {r.calories} kcal</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[{ l: 'P', v: r.protein, c: 'var(--brand-primary)' }, { l: 'C', v: r.carbs, c: '#00F0FF' }, { l: 'G', v: r.fats, c: '#FDE047' }].map((m, mi) => (
                        <div key={mi} className="text-center rounded-md py-1" style={{ background: 'rgba(0,0,0,0.4)' }}>
                          <p className="text-[10px] text-white/60 uppercase">{m.l}</p>
                          <p className="text-sm font-bold" style={{ color: m.c }}>{m.v}g</p>
                        </div>
                      ))}
                    </div>
                    {/* 2026-02 · Ingredientes principales agrupados por categoría (todos, con gramos). */}
                    <MainIngredientsList ingredients={r.ingredients} mainIngredients={r.main_ingredients} />
                    {/* 2026-10-04 · Aviso de déficit de macro en la tarjeta (antes solo en el modal). */}
                    <CloseMacroCardWarning recipe={r} batchId={currentBatch?.id} onUpdated={() => { fetchAllBatches(); }} />
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => openDetail(r)}
                        className="flex-1 min-w-[140px] text-center py-2 rounded-full text-sm font-semibold text-white hover:scale-[1.01] transition-all"
                        style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }} data-testid={`view-recipe-${i}`}>
                        Ver preparación
                      </button>
                      {/* 2026-02 · Biblioteca de recetas en modo REEMPLAZO:
                          abre el modal pasando mode="replace", batchId y
                          recipe.id. Al seleccionar, se sustituye la receta
                          actual preservando el id del slot. */}
                      <button
                        onClick={() => setRecipeLibrary({
                          mealIndex: (currentBatch?.meal_position ? currentBatch.meal_position - 1 : 0),
                          mealTime: currentBatch?.meal_time || r.meal_time || selectedMeal,
                          batchId: currentBatch?.id,
                          targetRecipeId: r.id,
                        })}
                        className="px-3 py-2 rounded-full text-xs text-white transition-all hover:scale-105 flex items-center gap-1"
                        style={{ background: 'rgba(0,240,255,0.12)', border: '1px solid rgba(0,240,255,0.4)' }}
                        title="Elegir una receta de la biblioteca para REEMPLAZAR ésta"
                        data-testid={`recipe-library-btn-${i}`}
                      >
                        📚 Biblioteca de recetas
                      </button>
                      {canRegenerate && (
                        <>
                          <button onClick={() => regenerateOne(r, { permanent: false })} disabled={!!regenerating[r.id]}
                            className="px-3 py-2 rounded-full text-sm text-white transition-all hover:scale-105 disabled:opacity-50"
                            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
                            title="Rotar por otra receta (podrá volver a aparecer más adelante)"
                            data-testid={`rotate-btn-${i}`}>
                            <FaSyncAlt className={regenerating[r.id] ? 'animate-spin' : ''} />
                          </button>
                          <button onClick={() => regenerateOne(r, { permanent: true })} disabled={!!regenerating[r.id]}
                            className="px-3 py-2 rounded-full text-sm text-white transition-all hover:scale-105 disabled:opacity-50"
                            style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)' }}
                            title="No me gusta: nunca volverá a aparecer en tu plan"
                            data-testid={`dislike-btn-${i}`}>
                            <FaThumbsDown className={regenerating[r.id] ? 'animate-pulse text-red-300' : 'text-red-300'} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          </>
        )}

        {/* Modal detalle */}
        {detail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={() => setDetail(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-3xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.15)' }} onClick={(e) => e.stopPropagation()}>
              {detail.image && (<div className="h-56 relative" style={{ background: `url(${detail.image}) center/cover` }}><button onClick={() => setDetail(null)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center"><FaTimes /></button></div>)}
              <div className="p-6">
                {!detail.image && (<div className="flex justify-end -mt-2 -mr-2"><button onClick={() => setDetail(null)} className="text-white/60 hover:text-white p-1"><FaTimes /></button></div>)}
                <h2 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{detail.name}</h2>
                <div className="flex items-center gap-4 text-sm text-white/70 mb-4">
                  <span className="flex items-center gap-1"><FaClock /> {detail.prep_minutes} min</span>
                  <span className="flex items-center gap-1"><FaFire /> {detail.calories} kcal</span>
                  <span className="text-pink-400">{detail.protein}P · {detail.carbs}C · {detail.fats}G</span>
                </div>
                <div className="mb-5">
                  <p className="text-[11px] uppercase tracking-widest font-bold text-pink-300/90 mb-2">Ingredientes</p>
                  <ul className="space-y-1.5">
                    {(detail.ingredients || []).map((ing, i) => (
                      <li key={i} className="flex justify-between text-sm text-white/85 border-b border-white/5 pb-1.5">
                        <span>{normalizeIngredientName(ing.name)}</span><span className="text-white/60">{formatAmount(ing.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {(detail.substitutions || []).length > 0 && (
                  <div className="mb-5 rounded-xl p-3" style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.35)' }} data-testid="detail-substitutions">
                    <p className="text-[11px] uppercase tracking-widest font-bold text-amber-300 mb-2">💡 Sustituciones sugeridas</p>
                    <ul className="space-y-1.5">
                      {detail.substitutions.map((s, i) => (
                        <li key={i} className="flex gap-2 items-start text-sm text-amber-100/90" data-testid={`detail-substitution-${i}`}>
                          <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5" style={{ background: 'rgba(245, 158, 11, 0.2)', color: 'rgb(252, 211, 77)' }}>{s.allergen}</span>
                          <span>{s.suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-bold text-pink-300/90 mb-2">Preparación</p>
                  <ol className="space-y-2">
                    {(detail.instructions || []).map((step, i) => (
                      <li key={i} className="flex gap-3 text-sm text-white/85">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}>{i + 1}</span>
                        <span className="pt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
      {/* 2026-02 · Biblioteca de recetas (modo REEMPLAZO para Fit Evolution) */}
      {recipeLibrary && (
        <RecipeLibraryModal
          isOpen={!!recipeLibrary}
          onClose={() => setRecipeLibrary(null)}
          mode="replace"
          mealIndex={recipeLibrary.mealIndex}
          mealTime={recipeLibrary.mealTime}
          dayType={selectedMeal}
          batchId={recipeLibrary.batchId}
          targetRecipeId={recipeLibrary.targetRecipeId}
          onAdded={() => { fetchAllBatches(); }}
        />
      )}
    </div>
  );
}

function FavoriteCard({ f, idx, onOpen }) {
  const r = f.recipe || {};
  const baseImg = (r.image_url || '').trim();
  // Ticket 280983: respaldo si la favorita no trae URL pero el plan si tiene foto.
  const [lazyImg, setLazyImg] = useState('');
  useEffect(() => {
    if (baseImg || !f.has_image || !f.batch_id || !f.recipe_id) return undefined;
    let alive = true;
    axios.get(`${API}/api/personalized-plans/fit-evolution/recipes/image/${f.batch_id}/${f.recipe_id}`)
      .then(res => {
        const url = (res.data?.image_url || '').trim();
        const b64 = res.data?.image_base64;
        let src = '';
        if (url) src = url.startsWith('http') ? url : `https://${url.replace(/^\/+/, '')}`;
        else if (b64) src = b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
        if (alive && src) setLazyImg(src);
      })
      .catch(() => { /* silencioso: queda el icono */ });
    return () => { alive = false; };
  }, [baseImg, f.has_image, f.batch_id, f.recipe_id]);
  const img = baseImg || lazyImg;
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(26,26,26,0.9)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid={`fav-card-${idx}`}>
      <div className="h-32 flex items-center justify-center"
        style={img
          ? { background: `url(${img}) center/cover` }
          : { background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow),0.15), rgba(0,240,255,0.06))' }}>
        {!img && <FaHeart className="text-3xl text-pink-400/80" />}
      </div>
      <div className="p-4">
        <p className="text-[10px] uppercase tracking-wider text-pink-300/80 mb-1">{f.meal_time}</p>
        <h3 className="text-white font-black text-base mb-2 line-clamp-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{r.name}</h3>
        <div className="flex items-center gap-3 text-[11px] text-white/70 mb-3">
          <span className="flex items-center gap-1"><FaClock /> {r.prep_minutes} min</span>
          <span className="flex items-center gap-1"><FaFire /> {r.calories} kcal</span>
          <span className="text-pink-400">{r.protein}P · {r.carbs}C · {r.fats}G</span>
        </div>
        <button onClick={() => onOpen(img)}
          className="w-full text-center py-2 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.01]"
          style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}>
          Ver preparación
        </button>
      </div>
    </div>
  );
}
